import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { planningReadinessBridge } from '../services/planning-readiness-bridge';
import { plannerTourBridge } from '../services/planner-tour-bridge';
import {
  OPEN_REQ_ROW_SELECTOR,
  reattachOpenRequirementsAnchors,
} from '../config/planner-anchor-selectors';

/**
 * MENTOR — Endstrang R1+R2 (§9.4/§9.5): Ernte-Reveal → besetzen →
 * Zwei-Blicke-Beweis → Go-live entscheiden → Kompetenz zeigen/loslassen.
 *
 * Gestartet aus dem Muster-Zweig (mentor-pattern, A.7), dem Resume-Schnellpfad
 * (mentor-entry, mentor-w1-continue) und dem Einzelschicht-Zweig (E.9 „Nein,
 * einmalig"). Schließt die Mentor-Reise ab.
 *
 * 2026-06-16: Der frühere eigenständige 1-Schritt-Tour MentorReunite (R1
 * „Schau, was gerade passiert ist") ist als ERSTER Schritt (r1-harvest) HIER
 * eingegliedert — so ist die Lücken-Reise EIN durchgehender Fluss mit einem
 * Zähler statt drei verketteter Mini-Touren (Resume → R1 → Endgame), die je
 * bei „1/1" zurücksprangen und ein vorzeitiges „Abschließen" zeigten. r1-harvest
 * läuft im selben 👤-Mitarbeiter-Modus wie der Rest (kein View-Sprung mehr) und
 * entfällt via skipIf, wenn gar kein Muster existiert (Einzelschicht-Pfad).
 *
 * AP-6 / 6e. Geführte Erklär-Schritte an stabilen/vorhandenen Ankern; die tiefere
 * interaktive Besetzung (GapPopover mit Undo) + die Freigeben-Dialog-Begleitung
 * werden in AP-7/AP-8 vertieft.
 */
function snap() {
  return planningReadinessBridge.snapshot();
}

/**
 * Hat der Nutzer im offenen Lücken-Popover gerade tatsächlich jemanden
 * zugewiesen? Liest den Gapline-Zähler („x von y … besetzt", Anker
 * `gap-popover-gapline`): die erste Zahl > 0 ⇒ mindestens ein Platz besetzt.
 * Damit verzweigt der Bestätigungs-Schritt R2.2b (r2-assigned) sauber zwischen
 * „Geschafft" (echte Zuweisung) und „Probier's aus" (der Nutzer hat per Tour-
 * „Weiter" übersprungen, ohne zuzuweisen). Defensiv false, wenn das Popover/
 * die Gapline fehlt.
 */
function gapJustFilled(): boolean {
  if (typeof document === 'undefined') return false;
  const line = document.querySelector('[onboardingAnchor="gap-popover-gapline"]');
  if (!line) return false;
  const m = ((line as HTMLElement).innerText || '').match(/\d+/);
  return !!m && parseInt(m[0], 10) > 0;
}

// ── R2.2 „Eine Lücke besetzen" — Klick-Affordanz auf der ersten Lücken-Kachel ──
let _gapHintEl: HTMLDivElement | null = null;
let _gapHintRaf = 0;

/**
 * R2.2 (r2-assign) ist interaktiv: die Tour hat kein „Weiter", sie wartet, bis
 * der Nutzer wirklich eine Lücken-Kachel anklickt (tour:gap-tile-clicked). Der
 * Spotlight liegt aber auf der GANZEN Unbesetzte-Schichten-Zeile (To-do-Liste),
 * sodass nicht eindeutig ist, WO genau zu klicken ist (User-Feedback
 * 2026-06-17: „eine zusätzliche Hilfe, wo man klicken soll … eine Kachel
 * highlighten"). Diese Affordanz legt einen pulsierenden Ring + eine tippende
 * Hand auf die visuell ERSTE (linkeste) klickbare Lücken-Kachel und führt sie
 * per requestAnimationFrame nach, falls der Planer scrollt oder neu rendert.
 *
 * Rein visuell: `pointer-events: none` (der Klick geht ungehindert auf die
 * Kachel durch) und KEIN Text → sprachneutral, deckt DE+EN ohne i18n ab.
 *
 * Selbst-Abbruch: Der rAF-Loop entfernt die Affordanz, sobald kein
 * `app-tour-popover` mehr im DOM ist (Esc/X/Abschluss). Das ist nötig, weil der
 * TourCoordinator `onAfterHide` beim Weiterschalten NICHT zuverlässig ruft
 * (gleiche Einschränkung wie bei den ⋮-Menü-Schritten oben). Auf dem regulären
 * Weiterschalt-Pfad räumt zusätzlich der Folge-Schritt (r2-popover) explizit
 * via onBeforeShow auf.
 */
function showGapClickHint(): void {
  if (typeof document === 'undefined') return;
  hideGapClickHint(); // idempotent — evtl. verwaiste Affordanz zuerst entfernen

  const hint = document.createElement('div');
  hint.className = 'tour-gap-hint';
  hint.setAttribute('aria-hidden', 'true');
  hint.innerHTML =
    '<span class="tour-gap-hint__ring"></span>' +
    '<i class="fa-solid fa-hand-pointer tour-gap-hint__hand"></i>';
  document.body.appendChild(hint);
  _gapHintEl = hint;

  // Visuell linkeste Lücken-Kachel in der Unbesetzte-Schichten-Lane
  // (data-resource-id^="-" = Spezial-Resource mit negativer ID). Bevorzugt
  // Kacheln mit `.gap-slot__body` (echte Lücken), fällt sonst auf die erste
  // Kachel überhaupt zurück. Manuelle Iteration statt `:has()` — bulletproof
  // über alle WebView-Engines.
  const findTile = (): HTMLElement | null => {
    const lane = document.querySelector('.fc-timeline-lane[data-resource-id^="-"]');
    if (!lane) return null;
    const all = Array.from(
      lane.querySelectorAll<HTMLElement>('.fc-timeline-event'),
    );
    // Bevorzugt eine LEERE (rote, 0 besetzt) Lücke — konsistent mit der roten
    // Beispiel-Kachel in der Tooltip-Abbildung; sonst die erste Teil-Lücke,
    // sonst die erste Kachel überhaupt.
    const empties = all.filter((e) => e.querySelector('.gap-slot__body--empty'));
    const gaps = all.filter((e) => e.querySelector('.gap-slot__body'));
    const pool = empties.length ? empties : gaps.length ? gaps : all;
    let best: HTMLElement | null = null;
    let bestLeft = Infinity;
    for (const e of pool) {
      const left = e.getBoundingClientRect().left;
      if (left < bestLeft) {
        bestLeft = left;
        best = e;
      }
    }
    return best;
  };

  // Selbst-Abbruch erst, NACHDEM das Popover einmal sichtbar war und wieder
  // verschwindet. onBeforeShow läuft VOR dem Popover-Mount — ein naives
  // „kein Popover → entfernen" würde die Affordanz sofort im ersten Frame
  // wieder killen (Popover noch nicht im DOM). Die Latch wartet aufs erste
  // Erscheinen und räumt dann bei Tour-Ende (Esc/X/Abschluss) auf.
  let seenPopover = false;
  const frame = () => {
    const hasPopover = !!document.querySelector('app-tour-popover');
    if (hasPopover) seenPopover = true;
    else if (seenPopover) {
      hideGapClickHint();
      return;
    }
    if (_gapHintEl) {
      const r = findTile()?.getBoundingClientRect();
      if (r && r.width > 0 && r.height > 0) {
        _gapHintEl.style.display = 'block';
        _gapHintEl.style.left = `${r.left}px`;
        _gapHintEl.style.top = `${r.top}px`;
        _gapHintEl.style.width = `${r.width}px`;
        _gapHintEl.style.height = `${r.height}px`;
      } else {
        _gapHintEl.style.display = 'none';
      }
    }
    _gapHintRaf = requestAnimationFrame(frame);
  };
  _gapHintRaf = requestAnimationFrame(frame);
}

/** Entfernt die Lücken-Klick-Affordanz und stoppt den rAF-Loop (idempotent). */
function hideGapClickHint(): void {
  if (_gapHintRaf) {
    cancelAnimationFrame(_gapHintRaf);
    _gapHintRaf = 0;
  }
  _gapHintEl?.remove();
  _gapHintEl = null;
}

/**
 * onBeforeShow-Helfer für die beiden „Verwalten"-Schritte (R2.4a/b): wechselt
 * — falls nötig — automatisch in den gewünschten Planer-Blick (Klick auf den
 * echten 👤/▦-Umschalter-Button) und ÖFFNET danach das ⋮-Overflow-MatMenü über
 * die `plannerTourBridge`, damit die Menü-Einträge sichtbar + spotlight-bar
 * sind. Setzt das Anker-Attribut aufs geöffnete Panel (wie in der
 * ShiftAssignment-Hilfe). So „swiped" die Tour synchron zum Voice-Over in den
 * passenden Blick und zeigt genau, worüber gesprochen wird. Geschlossen wird
 * das Menü vom Folge-Schritt (onAfterHide ist beim Weiterschalten unzuverlässig).
 */
function switchViewAndOpenOverflow(view: 'muster' | 'mitarbeiter'): Promise<void> {
  return new Promise<void>((resolve) => {
    if (typeof document === 'undefined') {
      resolve();
      return;
    }
    plannerTourBridge.closeOverflowMenu();
    plannerTourBridge.closePatternRowMenu();
    let cur: string | null = null;
    try {
      cur = localStorage.getItem('planner.viewMode');
    } catch {
      /* ignore */
    }
    const needsSwitch = cur !== view;
    if (needsSwitch) {
      const sel =
        view === 'muster'
          ? '.fc-viewPatternsButton-button'
          : '.fc-viewEmployeesButton-button';
      (document.querySelector(sel) as HTMLElement | null)?.click();
    }
    // Nach einem Blick-Wechsel rendert der Planer (FullCalendar) neu — kurz
    // warten, bevor das Overflow-Menü geöffnet wird.
    setTimeout(
      () => {
        plannerTourBridge.openOverflowMenu();
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            document
              .querySelector('.planner-overflow-menu')
              ?.setAttribute('onboardingAnchor', 'shift-overflow-menu');
            // Per-Eintrag-Anker imperativ setzen (der Planer setzt ALLE seine
            // Onboarding-Anker per setAttribute, nicht per Direktive — ein
            // `[attr.onboardingAnchor]` im Template greift hier nicht). So kann
            // der Voice-Over-Cue jeden Menüpunkt einzeln anpulsen, während er
            // ihn nennt. Zuordnung über das (eindeutige) FontAwesome-Icon des
            // Eintrags — sprach-/label-unabhängig.
            const iconToAnchor: Record<string, string> = {
              'fa-trash-can': 'overflow-item-delete',
              'fa-flag-checkered': 'overflow-item-horizonExtend',
              'fa-eye': 'overflow-item-horizonPreview',
              'fa-rotate-left': 'overflow-item-unpublish',
            };
            document
              .querySelectorAll('.planner-overflow-menu button')
              .forEach((btn) => {
                const icon = btn.querySelector('i');
                if (!icon) return;
                for (const [fa, anchorId] of Object.entries(iconToAnchor)) {
                  if (icon.classList.contains(fa)) {
                    btn.setAttribute('onboardingAnchor', anchorId);
                    break;
                  }
                }
              });
            resolve();
          }),
        );
      },
      needsSwitch ? 500 : 0,
    );
  });
}

/**
 * onBeforeShow-Helfer für den Schritt „einzelnes Muster verwalten": stellt den
 * ▦-Muster-Blick sicher, schließt das ⋮-Toolbar-Menü und ÖFFNET das
 * ⋯-Kontextmenü der ersten Muster-Zeile (plannerTourBridge.openPatternRowMenu)
 * — so SIEHT der Nutzer die Aktionen Bearbeiten/Pausieren/Löschen, statt sie
 * nur erklärt zu bekommen. Setzt den Panel-Anker (.ptn-row-menu) + die
 * Eintrag-Anker imperativ per Icon (der Planer nutzt überall setAttribute,
 * kein `[onboardingAnchor]`-Template-Binding). So pulst der Voice-Over-Cue
 * jeden Eintrag einzeln, während er ihn nennt.
 */
function openPatternRowMenuForTour(): Promise<void> {
  return new Promise<void>((resolve) => {
    if (typeof document === 'undefined') {
      resolve();
      return;
    }
    plannerTourBridge.closeOverflowMenu();
    let cur: string | null = null;
    try {
      cur = localStorage.getItem('planner.viewMode');
    } catch {
      /* ignore */
    }
    const needsSwitch = cur !== 'muster';
    if (needsSwitch) {
      (
        document.querySelector('.fc-viewPatternsButton-button') as HTMLElement | null
      )?.click();
    }
    setTimeout(
      () => {
        plannerTourBridge.openPatternRowMenu();
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            const panel = document.querySelector('.ptn-row-menu');
            panel?.setAttribute('onboardingAnchor', 'pattern-row-menu-panel');
            const iconToAnchor: Record<string, string> = {
              'fa-pen-to-square': 'pattern-row-edit',
              'fa-pause': 'pattern-row-pause',
              'fa-play': 'pattern-row-pause',
              'fa-trash-can': 'pattern-row-delete',
            };
            panel?.querySelectorAll('button').forEach((btn) => {
              const icon = btn.querySelector('i');
              if (!icon) return;
              for (const [fa, anchorId] of Object.entries(iconToAnchor)) {
                if (icon.classList.contains(fa)) {
                  btn.setAttribute('onboardingAnchor', anchorId);
                  break;
                }
              }
            });
            resolve();
          }),
        );
      },
      needsSwitch ? 500 : 0,
    );
  });
}
export const MENTOR_ENDGAME_TOUR: TourConfig = {
  id: TourFeature.MentorEndgame,
  version: 1,
  title: 'TUTORIAL.MENTOR.ENDGAME.TITLE',
  description: 'TUTORIAL.MENTOR.ENDGAME.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // 👤-Modus erzwingen: R2.2 „Lücke besetzen" ankert an der
  // Unbesetzte-Schichten-Zeile (open-requirements-row), die es nur im
  // Mitarbeiter-Modus gibt. Aus R1 (▦) kommend muss aktiv umgeschaltet werden.
  triggerContext: { route: '/planner?view=mitarbeiter' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: true,
    cooldownHoursAfterDismiss: 72,
    cooldownHoursAfterSnooze: 24,
    suppressAfterCompletion: false,
  },
  priority: 240,
  estimatedDurationSec: 180,
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  launchContext: { route: '/planner?view=mitarbeiter' },
  chapters: [
    {
      id: 'endgame',
      title: 'TUTORIAL.MENTOR.ENDGAME.TITLE',
      steps: [
        {
          // R1 — Ernte-Reveal: die App hat aus dem Muster konkrete Termine
          // erzeugt (Ampel-Farben). Früher eine eigene 1-Schritt-Tour
          // (MentorReunite); jetzt der erste Schritt des Endstrangs, damit der
          // ganze Lücken-Fluss EIN durchgehender Zähler ist (kein „1/1 →
          // Abschließen → 1/6"-Sprung mehr). Läuft im 👤-Mitarbeiter-Modus
          // (launchContext) — die Coverage-Farben sind dort an den
          // Unbesetzte-Schichten-Kacheln und den Tages-Balken sichtbar, daher
          // kein Wechsel in den ▦-Muster-Modus nötig.
          //
          // skipIf: Nur zeigen, wenn überhaupt ein Muster existiert. Im
          // Einzelschicht-Pfad (E.9 „Nein, einmalig" → direkt hierher) gibt es
          // kein Muster, dann wäre „aus deinem Muster erzeugt" falsch — der
          // Schritt entfällt und der Endstrang beginnt regulär bei r2-intro.
          // renumberVisibleSteps blendet ihn aus der Zählung aus.
          id: 'r1-harvest',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.REUNITE.R1.TITLE',
          // Variante datengetrieben — Lücken-Default bei unbekanntem Snapshot:
          // Ein frisch erzeugtes Muster hat fast immer offene Lücken, und
          // fälschlich „alles besetzt" (NO_GAP) zu behaupten ist der
          // irreführendere Fehler. Nur ein EXPLIZIT lückenfreier Snapshot
          // (s gesetzt && !hasPatternWithGap) zeigt die NO_GAP-Variante.
          content: () => {
            const s = snap();
            return s && !s.hasPatternWithGap
              ? 'TUTORIAL.MENTOR.REUNITE.R1.CONTENT_NO_GAP'
              : 'TUTORIAL.MENTOR.REUNITE.R1.CONTENT';
          },
          placement: 'auto',
          showNextButton: true,
          showPrevButton: false,
          skipIf: () => {
            const s = snap();
            return !s || !s.hasPattern;
          },
        },
        {
          // R2.1 — Einstieg in den Endstrang (stabiler Anker als erster Schritt).
          id: 'r2-intro',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.ENDGAME.R2_1.TITLE',
          content: 'TUTORIAL.MENTOR.ENDGAME.R2_1.CONTENT',
          placement: 'auto',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // R2.2 — INTERAKTIV: „Klick eine Lücken-Kachel". Kein „Weiter" — die
          // Tour springt erst weiter, wenn der Nutzer wirklich eine Kachel
          // klickt (öffnet das Kandidaten-Popover, feuert tour:gap-tile-clicked).
          // KEINE Farb-Legende mehr hier — die gehört zum Popover (R2.2a), das
          // die Kandidaten ZEIGT. Skippt, wenn keine Unbesetzte-Schichten-Zeile
          // da ist. strictPlacement → Popover bleibt UNTER der breiten Zeile.
          id: 'r2-assign',
          anchor: AnchorId.OpenRequirementsRow,
          spotlightAnchor: AnchorId.OpenRequirementsRow,
          additionalSpotlightAnchors: [AnchorId.OpenRequirementsLane],
          title: 'TUTORIAL.MENTOR.ENDGAME.R2_2.TITLE',
          content: 'TUTORIAL.MENTOR.ENDGAME.R2_2.CONTENT',
          // Abbildung im Tooltip: zeigt EXEMPLARISCH, wie eine unbesetzte (rote,
          // gestrichelte) Lücken-Kachel aussieht — „0/2", Zeitbereich, Klick-
          // Cursor —, damit sofort klar ist, WAS mit „Kachel" gemeint ist und
          // wo zu klicken (User-Feedback 2026-06-17). inlineSvg=true → die
          // Theme-Farben (Dark) greifen via Popover-Klasse; sprachneutral
          // (nur Zahlen), daher kein i18n-Bedarf in der Grafik.
          media: {
            type: 'svg',
            src: 'onboarding/visuals/mentor-endgame/r2-assign.svg',
            inlineSvg: true,
            // Dekorativ: die Grafik wiederholt nur visuell, was der Text +
            // der On-Canvas-Ring schon sagen → Screenreader überspringt sie
            // (alt='' / role=presentation). Vermeidet einen hartkodierten
            // Alt-Text (das Popover übersetzt `alt` NICHT).
            alt: '',
            decorative: true,
            dimensions: { width: 452, height: 84 },
            maxHeight: 64,
            loading: 'eager',
          },
          placement: 'bottom',
          strictPlacement: true,
          showNextButton: false,
          advanceOnDocumentEvent: 'tour:gap-tile-clicked',
          // Zusätzliche „hier klicken"-Affordanz: pulsierender Ring + tippende
          // Hand auf der ersten Lücken-Kachel (der breite Zeilen-Spotlight zeigt
          // die To-do-Liste, nicht WO zu klicken ist). Aufräumen macht der
          // Folge-Schritt (r2-popover.onBeforeShow) bzw. der Selbst-Abbruch im
          // rAF-Loop bei Tour-Ende — onAfterHide ist beim Weiterschalten unsicher.
          // Anchor SELBST neu setzen, bevor der Step rendert (25.08.2026).
          // FullCalendar verwirft das imperativ gesetzte Attribut bei jedem
          // Neuaufbau der Zeilen, und der Wechsel in den 👤-Blick erzeugt die
          // Timeline-Komponente neu. Gemessen entsteht dabei ein Fenster von
          // ~170 ms, in dem die Zeile da ist, das Attribut aber fehlt
          // (0,57 s Zeile / 0,74 s Attribut). Genau dieses Muster fehlte hier —
          // `sa-open-requirements` in shift-assignment-help hatte es, und das
          // war die einzige Tour, die in der Produktionsauswertung keinen
          // Schritt verlor. Details: config/planner-anchor-selectors.ts.
          onBeforeShow: () => {
            reattachOpenRequirementsAnchors();
            showGapClickHint();
          },
          // Auf den CSS-Selektor pruefen, NICHT auf das Anchor-Attribut: das
          // Attribut ist fluechtig (siehe oben), die Zeile ist die Sache.
          skipIf: () =>
            typeof document !== 'undefined' &&
            !document.querySelector(OPEN_REQ_ROW_SELECTOR),
          // Zaehl-Stabilitaet: skipIf oben ist view-transient (die
          // Unbesetzte-Schichten-Zeile existiert nur im 👤-Blick), wuerde den
          // Popover-/Chip-Zaehler beim Wechsel in den ▦-Blick schwanken lassen.
          // Fuer die Nummerierung stabil ueber den Snapshot: der Schritt gehoert
          // zur Reise, sobald es ueberhaupt Lücken gibt.
          numberingSkip: () => !snap()?.hasPatternWithGap,
        },
        {
          // R2.2a — das gerade geöffnete Kandidaten-Popover erklären (statt
          // oberflächlich vorab). Beschreibt die ECHTEN, sichtbaren Affordances
          // der Kandidaten (2026-06-16 korrigiert — die Kandidaten sind bewusst
          // farb-neutral, KEINE Grün/Gelb/Rot-Ampel wie auf den Coverage-Kacheln):
          // „Passt direkt" (✓ qualifiziert · ✓ verfügbar) → Zuweisen; „Mit
          // Begründung" (Grund steht dabei) → Trotzdem…; „Gesperrt" → ausgegraut
          // ohne Button; + „qualifizierte fragen" + „Details". Kein skipIf auf das
          // Popover (Race: das Event feuert VOR dem Render) — der Anker-Wait
          // wartet aufs Popover; advanced zusätzlich bei echter Zuweisung.
          // placement: 'left' → Tooltip links neben das (rechts erscheinende)
          // Popover, ohne es zu verdecken.
          id: 'r2-popover',
          anchor: AnchorId.GapPopoverContainer,
          spotlightAnchor: AnchorId.GapPopoverContainer,
          additionalSpotlightAnchors: [AnchorId.GapPopoverCandidates],
          // Resume-Wiedereinstieg: das Kandidaten-Popover ist ephemer und beim
          // Kaltstart-Resume nicht offen. Statt in einen 8s-Anker-Timeout zu
          // laufen (User-Feedback 2026-06-17: „Tour fortsetzen tut nichts"),
          // springt die Recovery auf r2-assign zurück — den stabilen Schritt,
          // der das Popover durch einen Kachel-Klick wieder öffnet.
          resumeRecoveryStepId: 'r2-assign',
          title: 'TUTORIAL.MENTOR.ENDGAME.R2_2A.TITLE',
          content: 'TUTORIAL.MENTOR.ENDGAME.R2_2A.CONTENT',
          placement: 'left',
          showNextButton: true,
          showPrevButton: false,
          advanceOnDocumentEvent: 'tour:gap-popover-assignment-done',
          // Die „hier klicken"-Affordanz aus R2.2 deterministisch entfernen
          // (der Coordinator ruft onAfterHide des Vorschritts beim Vorwärts-
          // Schalten nicht zuverlässig) — die Lücke ist ja gerade geklickt.
          onBeforeShow: () => hideGapClickHint(),
        },
        {
          // R2.2b — Bestätigung (2026-06-16): Früher sprang die Tour direkt von
          // den Kandidaten (R2.2a) zum unverbundenen „Zwei-Blicke"-Konzept
          // (R2.3) und spotlightete einen ganz anderen Bereich — die gerade
          // gemachte Zuweisung wurde mit keinem Wort bestätigt (User-Feedback:
          // „kommt aus dem Nichts, kein Zusammenhang, prüft nicht ob ich
          // zugewiesen habe"). Dieser Schritt schließt die Lücke: er bleibt AM
          // Popover (Anker GapPopoverContainer, Spotlight auf die Gapline „x von
          // y besetzt") und bestätigt datengetrieben, dass der Platz jetzt
          // besetzt ist. gapJustFilled() verzweigt Titel/Text/Voice-Over:
          // echte Zuweisung → „Geschafft"; per Tour-„Weiter" übersprungen →
          // „Probier's aus". Skippt automatisch, wenn das Popover nicht (mehr)
          // offen ist (Anker-Timeout) — dann geht es direkt zu R2.3.
          // showPrevButton:false — zurück in den verbrauchten Zuweis-Schritt
          // wäre ein toter Punkt.
          id: 'r2-assigned',
          anchor: AnchorId.GapPopoverContainer,
          spotlightAnchor: AnchorId.GapPopoverGapline,
          additionalSpotlightAnchors: [AnchorId.GapPopoverContainer],
          // Wie r2-popover: hängt am ephemeren Lücken-Popover → bei Resume
          // ohne offenes Popover zurück auf r2-assign (Kachel klicken).
          resumeRecoveryStepId: 'r2-assign',
          title: () =>
            gapJustFilled()
              ? 'TUTORIAL.MENTOR.ENDGAME.R2_2B.TITLE_DONE'
              : 'TUTORIAL.MENTOR.ENDGAME.R2_2B.TITLE_TRY',
          content: () =>
            gapJustFilled()
              ? 'TUTORIAL.MENTOR.ENDGAME.R2_2B.CONTENT_DONE'
              : 'TUTORIAL.MENTOR.ENDGAME.R2_2B.CONTENT_TRY',
          audioStepId: () =>
            gapJustFilled() ? 'r2-assigned-done' : 'r2-assigned-try',
          placement: 'left',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // R2.3 — Ein Plan, zwei Blicke: rein erklärender Schritt (kein
          // Handlungsaufruf). FRÜHER ankerte + spotlightete er gezielt den
          // 👤/▦-Umschalter — das wirkte wie „klick den Schalter jetzt" und
          // drängte zum Ansichtswechsel (User-Feedback 2026-06-16: „als ob man
          // unbedingt die Ansicht wechseln soll"). Jetzt liegt der Spotlight auf
          // dem GANZEN Plan (wie r2-intro) — die Botschaft ist „das hier ist EIN
          // Plan", nicht „bedien dieses Steuerelement". Der Text nennt den
          // Umschalter weiter neutral („oben") und stellt klar, dass man jetzt
          // nichts umschalten muss. Kein eigener spotlightAnchor → Default =
          // anchor (PlannerView), keine Hervorhebung eines einzelnen Controls.
          id: 'r2-proof',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.ENDGAME.R2_3.TITLE',
          content: 'TUTORIAL.MENTOR.ENDGAME.R2_3.CONTENT',
          placement: 'auto',
          showNextButton: true,
          // Kein „Zurück": der Vorgänger (r2-assigned/r2-popover) hängt am
          // Lücken-Popover, das hier i. d. R. schon geschlossen ist → ein
          // Zurück-Klick lief in einen 8-Sekunden-Anker-Timeout und endete als
          // toter Punkt (User-Feedback 2026-06-16). Der Endstrang läuft ab hier
          // bewusst nur vorwärts.
          showPrevButton: false,
        },
        {
          // R2.4 — Go-live bewusst entscheiden (Freigeben mit Empfängerprüfung).
          // Smart: Hat (bei vorhandenen Mitarbeitern) NIEMAND einen
          // Benachrichtigungsweg, weist die Variante darauf hin, dass „Freigeben
          // & benachrichtigen" zwar sichtbar macht, aber keine Nachricht
          // rausgeht (Snapshot hasNotifiableEmployee). Tooltip + Voice-Over
          // verzweigen. Default = Normaltext (auch bei null-Snapshot — kein
          // falscher Alarm).
          id: 'r2-publish',
          anchor: AnchorId.PublishButton,
          title: 'TUTORIAL.MENTOR.ENDGAME.R2_4.TITLE',
          content: () => {
            const s = snap();
            return s && s.totalActiveEmployees > 0 && !s.hasNotifiableEmployee
              ? 'TUTORIAL.MENTOR.ENDGAME.R2_4.CONTENT_NO_CHANNEL'
              : 'TUTORIAL.MENTOR.ENDGAME.R2_4.CONTENT';
          },
          audioStepId: () => {
            const s = snap();
            return s && s.totalActiveEmployees > 0 && !s.hasNotifiableEmployee
              ? 'r2-publish-nochannel'
              : 'r2-publish';
          },
          placement: 'bottom',
          showNextButton: true,
          skipIf: () =>
            typeof document !== 'undefined' &&
            !document.querySelector('[onboardingAnchor="publish-button"]'),
          // Zaehl-Stabilitaet: skipIf oben prueft den Freigeben-Button im DOM,
          // der beim Blickwechsel kurz verschwindet (Toolbar-Re-Render) — das
          // liess den Zaehler beim Muster-Schritt von 11 auf 10 springen. Der
          // Freigeben-Schritt gehoert IMMER zur Mentor-Reise, zaehlt also stabil.
          numberingSkip: () => false,
        },
        {
          // R2.4a — Verwalten & pflegen (Auffindbarkeit, 2026-06-16): Die Reise
          // lehrt bauen → besetzen → freigeben, aber nie die Lifecycle-Aktionen.
          // User-Feedback: „wo man ein Muster löschen kann, wird nirgends
          // thematisiert". Dieser Schritt zeigt WO Verwaltung wohnt — die Tiefe
          // bleibt in den jeweiligen ?-Hilfen (DeletePattern/CleanupPlanning/
          // UnpublishWeek).
          //
          // ⚠ Das ⋮-Toolbar-Menü ist VIEW-ABHÄNGIG (live verifiziert):
          //   ▦ Muster-Blick   → Legende, Horizont-Vorschau, Weitere Wochen,
          //                       Planung aufräumen
          //   👤 Mitarbeiter-Blick → Legende, Analyse, Export, Vorwoche kopieren,
          //                       Gespeicherte Wochen, Freigabe zurückziehen,
          //                       Schichten löschen
          // Darum als ZWEI geführte Schritte (User-Feedback 2026-06-16: „jede
          // Sache, die das Voice-Over nennt, muss sichtbar gehighlightet sein;
          // beim Muster-Blick automatisch dorthin swipen"). Jeder Schritt
          // swiped via onBeforeShow in seinen Blick und ÖFFNET dort das
          // ⋮-Menü (plannerTourBridge) → die genannten Einträge sind sichtbar +
          // gespotlightet. Geschlossen wird das Menü vom Folge-Schritt.
          id: 'r2-manage-pattern',
          anchor: AnchorId.ShiftOverflowMenu,
          spotlightAnchor: AnchorId.ShiftOverflowMenu,
          title: 'TUTORIAL.MENTOR.ENDGAME.R2_4A.TITLE',
          content: 'TUTORIAL.MENTOR.ENDGAME.R2_4A.CONTENT',
          audioStepId: 'r2-manage-pattern',
          placement: 'left',
          showNextButton: true,
          showPrevButton: false,
          onBeforeShow: () => switchViewAndOpenOverflow('muster'),
          onAfterHide: () => plannerTourBridge.closeOverflowMenu(),
        },
        {
          // R2.4a-row — Einzelnes Muster verwalten: ÖFFNET das ⋯-Kontextmenü der
          // ersten Muster-Zeile (eigener Schritt, da ⋮-Toolbar- und ⋯-Zeilen-
          // Menü nicht gleichzeitig offen sein können). Der Nutzer SIEHT
          // Bearbeiten/Pausieren/Löschen, und jeder Eintrag pulst synchron zum
          // Voice-Over (User-Wunsch 2026-06-16: „nicht nur markieren, sondern
          // anklicken/öffnen und erklären"). Spotlight = geöffnetes Panel
          // (.ptn-row-menu); Folge-Schritt (employee) schließt es via
          // switchViewAndOpenOverflow → closePatternRowMenu.
          id: 'r2-manage-pattern-row',
          anchor: AnchorId.PatternRowMenuPanel,
          spotlightAnchor: AnchorId.PatternRowMenuPanel,
          title: 'TUTORIAL.MENTOR.ENDGAME.R2_4A_ROW.TITLE',
          content: 'TUTORIAL.MENTOR.ENDGAME.R2_4A_ROW.CONTENT',
          audioStepId: 'r2-manage-pattern-row',
          placement: 'right',
          showNextButton: true,
          showPrevButton: false,
          skipIf: () =>
            typeof document !== 'undefined' &&
            !document.querySelector('.ptn-row__menu-btn'),
          // Zaehl-Stabilitaet: skipIf oben ist view-transient (das
          // ⋯-Zeilenmenue gibt es nur im ▦-Muster-Blick), schwankt also je nach
          // Blick. Der Schritt wird aber via onBeforeShow-Swipe sicher gezeigt,
          // sobald ueberhaupt ein Muster existiert — dann zaehlt er stabil mit.
          numberingSkip: () => !snap()?.hasPattern,
          onBeforeShow: () => openPatternRowMenuForTour(),
          onAfterHide: () => plannerTourBridge.closePatternRowMenu(),
        },
        {
          // R2.4b — Mitarbeiter-Blick: dasselbe ⋮-Menü bietet hier die laufende
          // Pflege (Freigabe zurücknehmen, Schichten gebündelt löschen).
          // onBeforeShow swiped zurück in den 👤-Blick + öffnet das Menü.
          id: 'r2-manage-employee',
          anchor: AnchorId.ShiftOverflowMenu,
          spotlightAnchor: AnchorId.ShiftOverflowMenu,
          title: 'TUTORIAL.MENTOR.ENDGAME.R2_4B.TITLE',
          content: 'TUTORIAL.MENTOR.ENDGAME.R2_4B.CONTENT',
          audioStepId: 'r2-manage-employee',
          placement: 'left',
          showNextButton: true,
          showPrevButton: false,
          onBeforeShow: () => switchViewAndOpenOverflow('mitarbeiter'),
          onAfterHide: () => plannerTourBridge.closeOverflowMenu(),
        },
        {
          // R2.5 — Abschluss: bewusst loslassen (Prinzip 6). onBeforeShow
          // schließt das ggf. noch offene ⋮-Menü des Vorschritts zuverlässig.
          id: 'r2-finish',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.ENDGAME.R2_5.TITLE',
          content: 'TUTORIAL.MENTOR.ENDGAME.R2_5.CONTENT',
          placement: 'auto',
          showNextButton: true,
          showPrevButton: false,
          onBeforeShow: () => plannerTourBridge.closeOverflowMenu(),
        },
      ],
    },
  ],
};
